import React, { useState, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { Box, Button, TextField } from '@material-ui/core'
import ReCAPTCHA from 'react-google-recaptcha'
import clsx from 'clsx'
import { isNil } from 'lodash'
import PropTypes from 'prop-types'
import { reverse } from 'named-urls'

import useSnackbar from 'hooks/useSnackbar'
import useClientPortalTheme from 'hooks/useClientPortalTheme'

import { getGoogleRecaptchaToken, isProductionOrHomol } from 'service/env'
import constants from 'constants/index'
import schemas from '../../schemas'
import * as service from 'service'
import useStyles from './styles'
import { routes } from 'Routes'

const Form = ({
  whiteColor = false,
  email = '',
  enableRedirect = true,
  setClose,
}) => {
  const { PLATFORM_SLUG_ID } = constants.settings
  const [loading, setLoading] = useState(false)
  const [recaptcha, setRecaptcha] = useState(undefined)

  const recaptchaRef = useRef(null)

  const disableSubmitButton = isProductionOrHomol
    ? !recaptcha || loading
    : false

  const history = useHistory()
  const snackbar = useSnackbar()
  const { slug } = useParams()

  const { clientPortalTheme } = useClientPortalTheme()
  const classes = useStyles({ clientPortalTheme })()

  const { control, handleSubmit, errors } = useForm({
    validationSchema: schemas.forgotPassword,
    defaultValues: {
      email: email,
      password: '',
    },
  })

  const hasSlug =
    !isNil(slug) &&
    !constants.settings.DEFAULT_SLUGS_PATHS.includes(slug?.toLowerCase())

  const onSubmit = async (data) => {
    if (disableSubmitButton) return

    setLoading(true)

    try {
      const response = await service.dponet.auth.forgotPassword({
        ...data,
        ...(hasSlug && { publicPath: slug }),
        recaptcha,
      })

      snackbar.open({
        message: response.data.message,
        variant: 'success',
      })

      if (enableRedirect)
        return history.push(
          reverse(routes.root, { slug: slug || PLATFORM_SLUG_ID }),
        )

      setClose()
    } catch (error) {
      if (isProductionOrHomol) resetRecaptcha()
      snackbar.open({
        message: defineErrorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const resetRecaptcha = () => {
    recaptchaRef.current.reset()
    setRecaptcha(undefined)
  }

  const defineErrorMessage = (error) => {
    const response = error?.response
    const status = response?.status
    const errorMessage = response.data?.error?.message

    if ((status === 404 || status === 422) && errorMessage) return errorMessage

    return 'Ocorreu algum erro! Tente novamente!'
  }

  const handleAcceptedRecaptcha = (recaptchaToken) => {
    setRecaptcha(recaptchaToken)
  }
  const disableRecaptcha = () => setRecaptcha(undefined)

  const handleBack = () =>
    history.push(reverse(routes.root, { slug: slug || PLATFORM_SLUG_ID }))

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        as={
          <TextField
            label={!whiteColor && 'E-mail'}
            color="primary"
            variant="outlined"
            error={!!errors.email}
            helperText={errors?.email?.message}
            className={clsx({
              [classes.inputWhite]: whiteColor,
            })}
            fullWidth
          />
        }
        control={control}
        name="email"
        mode="onChange"
      />
      {isProductionOrHomol && (
        <Box mt={2} display="flex" alignItems="center" justifyContent="center">
          <ReCAPTCHA
            ref={recaptchaRef}
            render="explicit"
            sitekey={getGoogleRecaptchaToken()}
            onChange={handleAcceptedRecaptcha}
            onExpired={disableRecaptcha}
          />
        </Box>
      )}
      <Box mt={3} className={classes.actionButtons}>
        {enableRedirect && (
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={handleBack}
            className={classes.buttonBack}
          >
            Voltar
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={disableSubmitButton}
          className={clsx({
            [classes.buttonPrimaryWhite]: whiteColor,
            [classes.buttonPrimaryBlue]: !whiteColor,
          })}
        >
          {loading ? 'Enviando...' : 'Recuperar'}
        </Button>
      </Box>
    </form>
  )
}

Form.propTypes = {
  whiteColor: PropTypes.bool,
  email: PropTypes.string,
  enableRedirect: PropTypes.bool,
  setClose: PropTypes.func,
}

export default Form
